import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ClientContractSummary } from '../../domain/contracts/clientContractSummary';

interface ClientSummaryProps {
    /** The figures, computed from the contracts the page already holds. */
    summary: ClientContractSummary;
    /** The client's category, shown as the sixth figure. */
    category?: string | null;
    /** Whether a client is selected at all; with none, every figure reads as a dash. */
    hasClient: boolean;
}

/** Currency is shown without decimals, as everywhere else in the application. */
const formatRon = (amount: number) => `${amount.toFixed(0)} RON`;

/**
 * The administrators-only summary of the selected client, under the contracts grid.
 *
 * Read-only figures, not form fields: plain text in a `<Box>`, so there is nothing to focus,
 * click, edit or tab into, and the cursor stays the default arrow. They update with the
 * selected client like any other rendered value.
 *
 * It is a component of its own rather than a block inside the Contracts page because the six
 * "a client is selected, or a dash" decisions it makes were a seventh of that page's cognitive
 * complexity, for a section that depends on nothing else the page holds.
 */
const ClientSummary = ({ summary, category, hasClient }: ClientSummaryProps) => {
    const { t } = useTranslation();

    const orDash = (value: string) => (hasClient ? value : '-');

    const fields = [
        {
            key: 'contract-count',
            label: t('contracts.summary.contractCount'),
            value: orDash(String(summary.contractCount)),
        },
        {
            key: 'contract-value',
            label: t('contracts.summary.contractValue'),
            value: orDash(formatRon(summary.contractValue)),
        },
        {
            key: 'expired-count',
            label: t('contracts.summary.expiredCount'),
            value: orDash(String(summary.expiredCount)),
        },
        {
            key: 'expired-value',
            label: t('contracts.summary.expiredValue'),
            value: orDash(formatRon(summary.expiredValue)),
        },
        {
            key: 'net-profit',
            label: t('contracts.summary.netProfit'),
            value: orDash(formatRon(summary.netProfit)),
        },
        {
            key: 'category',
            label: t('contracts.summary.category'),
            value: String(category ?? '').trim() || '-',
        },
    ];

    return (
        <Paper
            data-testid="client-summary"
            elevation={0}
            sx={{
                mt: 1.5,
                p: 1,
                display: 'flex',
                gap: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                backgroundColor: '#fff',
            }}
        >
            {fields.map(field => (
                <Box key={field.key} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        component="div"
                        color="text.secondary"
                        sx={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}
                    >
                        {field.label}
                    </Typography>
                    <Typography
                        variant="body2"
                        component="div"
                        data-testid={`client-summary-${field.key}`}
                        sx={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {field.value}
                    </Typography>
                </Box>
            ))}
        </Paper>
    );
};

export default ClientSummary;
